import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS, BACKEND_NODE_URL } from '../config/constants';
import './CRMFollowupListConfigModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de configuração específico para as listas de Follow-up (Follow-up 1, 2, 3)
 * Contém:
 * - Ferramenta de Mensagem Automática (envia msg quando contato entra na lista)
 * - Ferramenta de Follow-up Automático (move para outra lista após X horas)
 */
// Ícones disponíveis para listas
const LIST_ICONS = [
  'list', 'inbox', 'person', 'group', 'star', 'favorite', 'chat', 'forum',
  'call', 'mail', 'schedule', 'task_alt', 'check_circle', 'flag', 'label',
  'bookmark', 'trending_up', 'shopping_cart', 'payments', 'handshake',
  'support_agent', 'rocket_launch', 'celebration', 'warning', 'block',
  'hourglass_top', 'timer', 'replay', 'sync', 'bolt', 'diamond'
];

// Cores disponíveis para listas
const LIST_COLORS = [
  '#b43bea', '#8b5cf6', '#6366f1', '#3b82f6', '#0ea5e9', '#06b6d4',
  '#14b8a6', '#10b981', '#22c55e', '#84cc16', '#eab308', '#f59e0b',
  '#f97316', '#ef4444', '#ec4899', '#d946ef', '#64748b', '#374151'
];

export default function CRMFollowupListConfigModal({ list, onClose, onSave, onDelete, projectId }) {
  const [loading, setLoading] = useState(false);
  const [connection, setConnection] = useState(null);

  // ✅ Estados para edição de nome, cor e ícone da lista
  const [listName, setListName] = useState(list?.title || '');
  const [listColor, setListColor] = useState(list?.color || '#b43bea');
  const [listIcon, setListIcon] = useState(list?.icon || 'list');
  const [savingListInfo, setSavingListInfo] = useState(false);

  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const MAX_IMAGES = 3;
  const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB

  const [initialAudioEnabled, setInitialAudioEnabled] = useState(false);
  const [initialAudioUrl, setInitialAudioUrl] = useState('');
  const [audioPreviewUrl, setAudioPreviewUrl] = useState('');
  const [blockAgentResponseForInitialAudio, setBlockAgentResponseForInitialAudio] = useState(true);
  const [audioRecording, setAudioRecording] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioObjectUrlRef = useRef(null);

  // Mensagem Automática States
  const [autoMessageEnabled, setAutoMessageEnabled] = useState(false);
  const [autoMessage, setAutoMessage] = useState('');
  const [savingAutoMessage, setSavingAutoMessage] = useState(false);

  // Follow-up Rules States
  const [followUpRules, setFollowUpRules] = useState([]);
  const [projectLists, setProjectLists] = useState([]);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [newRule, setNewRule] = useState({ targetListId: '', delayHours: 24 });
  const [loadingRules, setLoadingRules] = useState(false);
  const [followUpEnabled, setFollowUpEnabled] = useState(false);

  // ✅ NOVO: Botão de Link States
  const [buttonLinkEnabled, setButtonLinkEnabled] = useState(false);
  const [buttonLinks, setButtonLinks] = useState([]);
  const [newButtonLabel, setNewButtonLabel] = useState('');
  const [newButtonUrl, setNewButtonUrl] = useState('');
  const [buttonLinkMessage, setButtonLinkMessage] = useState('Clique em um dos botões abaixo:');
  const [blockAgentResponseForButtonLink, setBlockAgentResponseForButtonLink] = useState(false);
  const MAX_BUTTONS = 4;

  // ✅ NOVO: Arquivamento Automático States
  const [archiveRules, setArchiveRules] = useState([]);
  const [archiveEnabled, setArchiveEnabled] = useState(false);
  const [archiveDelayHours, setArchiveDelayHours] = useState(48);
  const [archiveMode, setArchiveMode] = useState('scheduled'); // 'immediate' | 'scheduled'
  const [showArchiveForm, setShowArchiveForm] = useState(false);
  const [archivingAll, setArchivingAll] = useState(false);

  // ✅ NOVO: Encaminhar para Projeto States
  const [forwardEnabled, setForwardEnabled] = useState(false);
  const [forwardTargetProjectId, setForwardTargetProjectId] = useState('');
  const [forwardTargetListId, setForwardTargetListId] = useState('');
  const [forwardMode, setForwardMode] = useState('immediate'); // 'immediate' | 'scheduled'
  const [forwardDelayHours, setForwardDelayHours] = useState(1);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableLists, setAvailableLists] = useState([]);
  const [forwardConfig, setForwardConfig] = useState(null);
  const [loadingForward, setLoadingForward] = useState(false);

  // States para ferramenta de remarketing
  const [remarketingEnabled, setRemarketingEnabled] = useState(false);
  const [remarketingListName, setRemarketingListName] = useState('');
  const [remarketingClearAfterSave, setRemarketingClearAfterSave] = useState(false);
  const [remarketingAutoEnabled, setRemarketingAutoEnabled] = useState(false);
  const [remarketingTimerHours, setRemarketingTimerHours] = useState(24);
  const [savingRemarketing, setSavingRemarketing] = useState(false);

  // ✅ NOVO: Estados para notificação em grupo WhatsApp
  const [groupNotificationEnabled, setGroupNotificationEnabled] = useState(false);
  const [availableConnections, setAvailableConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupNotificationMessage, setGroupNotificationMessage] = useState('');
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [savingGroupNotification, setSavingGroupNotification] = useState(false);

  const getProjectConnectionIds = (project) => {
    if (!project?.settings) return [];
    const settings = project.settings;

    if (project.project_type === 'crm_multi') {
      return (settings.crm_multi?.connection_ids || []).filter(Boolean);
    }

    if (project.project_type === 'marketing_campaign') {
      return (settings.marketing_campaign?.connection_ids || []).filter(Boolean);
    }

    const singleConnectionId = settings.crm?.connection_id || settings.connection_id;
    return singleConnectionId ? [singleConnectionId] : [];
  };

  // Carregar conexão e agente do projeto
  useEffect(() => {
    const loadConnectionAndAgent = async () => {
      try {
        setLoadingAgent(true);
        
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings, company_id, project_type')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;

        const connectionIds = getProjectConnectionIds(project);
        const connectionId = connectionIds[0] || null;

        if (connectionId) {
          const { data: conn, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', connectionId)
            .single();

          if (connError) throw connError;
          setConnection(conn);
          
          // Buscar agente vinculado à conexão
          const { data: agent, error: agentError } = await supabase
            .from('agentes')
            .select('id, nome, modelo')
            .eq('conexao_id', connectionId)
            .eq('empresa_id', project.company_id)
            .single();
          
          if (!agentError && agent) {
            setAgentInfo(agent);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar conexão:', error);
      } finally {
        setLoadingAgent(false);
      }
    };

    if (projectId) {
      loadConnectionAndAgent();
    }
  }, [projectId]);

  // ✅ Carregar conexões disponíveis conforme tipo de projeto
  useEffect(() => {
    const loadAvailableConnections = async () => {
      try {
        setLoadingConnections(true);
        
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings, project_type')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;

        const connectionIds = getProjectConnectionIds(project);
        
        if (connectionIds.length > 0) {
          const { data: connections, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .in('id', connectionIds);

          if (connError) throw connError;
          setAvailableConnections(connections || []);
          
          // Selecionar primeira conexão por padrão
          if (connections && connections.length > 0) {
            setSelectedConnection(connections[0]);
          }
        } else {
          setAvailableConnections([]);
          setSelectedConnection(null);
        }
      } catch (error) {
        console.error('Erro ao carregar conexões:', error);
      } finally {
        setLoadingConnections(false);
      }
    };

    if (projectId) {
      loadAvailableConnections();
    }
  }, [projectId]);

  // ✅ NOVO: Carregar configuração de notificação em grupo da lista
  useEffect(() => {
    const loadGroupNotificationConfig = async () => {
      if (!list?.id) return;
      
      try {
        const { data: listData, error } = await supabase
          .from('project_lists')
          .select('metadata')
          .eq('id', list.id)
          .single();

        if (error) throw error;

        const md = listData?.metadata || {};
        const groupNotif = md.group_notification || {};
        
        setGroupNotificationEnabled(!!groupNotif.enabled);
        setGroupNotificationMessage(groupNotif.message || '');
        
        if (groupNotif.connection_id) {
          const { data: conn } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', groupNotif.connection_id)
            .single();
          
          if (conn) {
            setSelectedConnection(conn);
          }
        }
        
        if (groupNotif.group_jid && groupNotif.group_name) {
          setSelectedGroup({
            jid: groupNotif.group_jid,
            name: groupNotif.group_name
          });
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de notificação em grupo:', error);
      }
    };

    if (list?.id) {
      loadGroupNotificationConfig();
    }
  }, [list?.id]);

  // Carregar configuração de mensagem automática da lista
  useEffect(() => {
    const loadAutoMessageConfig = async () => {
      if (!list?.id) return;
      
      try {
        const { data: listData, error } = await supabase
          .from('project_lists')
          .select('automation_rules, metadata')
          .eq('id', list.id)
          .single();

        if (error) throw error;

        const rules = listData?.automation_rules || {};
        setAutoMessageEnabled(rules.auto_message_enabled || false);
        setAutoMessage(rules.auto_message || '');

        const md = listData?.metadata || {};
        const loadedImages = [];
        if (md?.initial_images && Array.isArray(md.initial_images)) {
          loadedImages.push(...md.initial_images);
        } else if (md?.initial_image) {
          loadedImages.push(md.initial_image);
        }
        setImages(loadedImages);
        if (md?.initial_audio_enabled !== undefined) {
          setInitialAudioEnabled(!!md.initial_audio_enabled);
        }
        if (md?.initial_audio_url) {
          setInitialAudioUrl(md.initial_audio_url);
          setAudioPreviewUrl(md.initial_audio_url);
        }
        if (md?.initial_audio_block_agent_response !== undefined) {
          setBlockAgentResponseForInitialAudio(md.initial_audio_block_agent_response !== false);
        }

        // ✅ NOVO: Carregar configurações de botão de link
        if (md?.button_link_enabled !== undefined) {
          setButtonLinkEnabled(!!md.button_link_enabled);
        }
        if (md?.button_links && Array.isArray(md.button_links)) {
          setButtonLinks(md.button_links);
        }
        if (md?.button_link_message) {
          setButtonLinkMessage(md.button_link_message);
        }
        if (md?.block_agent_response_for_button_link !== undefined) {
          setBlockAgentResponseForButtonLink(!!md.block_agent_response_for_button_link);
        }
      } catch (error) {
        console.error('Erro ao carregar configuração de mensagem automática:', error);
      }
    };

    loadAutoMessageConfig();
  }, [list?.id]);

  useEffect(() => {
    return () => {
      try {
        if (audioObjectUrlRef.current) {
          URL.revokeObjectURL(audioObjectUrlRef.current);
          audioObjectUrlRef.current = null;
        }
      } catch (_) {}
    };
  }, []);

  const getAudioMimeType = () => {
    try {
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg'
      ];
      for (const c of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
          return c;
        }
      }
    } catch (_) {}
    return '';
  };

  const parseStorageRefFromPublicUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const m = url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (m) return { bucket: m[1], path: m[2] };
      return null;
    } catch (_) {
      return null;
    }
  };

  const uploadRecordedAudio = async (blob) => {
    try {
      setAudioUploading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      if (!user?.company_id) throw new Error('Usuário sem company_id');

      const MAX_AUDIO_SIZE = 10 * 1024 * 1024;
      if (blob.size > MAX_AUDIO_SIZE) {
        throw new Error('Áudio muito grande. Limite: 10MB');
      }

      try {
        if (initialAudioUrl) {
          const ref = parseStorageRefFromPublicUrl(initialAudioUrl);
          if (ref) {
            await supabase.storage.from(ref.bucket).remove([ref.path]);
          }
        }
      } catch (_) {}

      const bucket = 'project-attachments';
      const ext = (blob.type || '').includes('ogg') ? 'ogg' : 'webm';
      const fileName = `crm-followup-audio/${user.company_id}/${projectId}/${list?.id || 'unknown'}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, blob, {
          cacheControl: '3600',
          upsert: true,
          contentType: blob.type || 'audio/webm'
        });

      if (uploadError) throw new Error(uploadError.message);

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl || '';
      if (!publicUrl) throw new Error('Falha ao obter URL pública do áudio');

      setInitialAudioUrl(publicUrl);
      setAudioPreviewUrl(publicUrl);
      setInitialAudioEnabled(true);
    } catch (error) {
      console.error('Erro ao salvar áudio:', error);
      alert('Erro ao salvar áudio: ' + error.message);
    } finally {
      setAudioUploading(false);
    }
  };

  const startAudioRecording = async () => {
    try {
      if (audioRecording) return;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getAudioMimeType();
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mr.onstop = async () => {
        try {
          stream.getTracks().forEach(t => t.stop());
        } catch (_) {}

        const chunks = audioChunksRef.current || [];
        audioChunksRef.current = [];
        if (chunks.length === 0) return;

        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size <= 0) return;

        try {
          if (audioObjectUrlRef.current) {
            URL.revokeObjectURL(audioObjectUrlRef.current);
            audioObjectUrlRef.current = null;
          }
          audioObjectUrlRef.current = URL.createObjectURL(blob);
          setAudioPreviewUrl(audioObjectUrlRef.current);
        } catch (_) {}

        await uploadRecordedAudio(blob);
      };

      mediaRecorderRef.current = mr;
      mr.start();
      setAudioRecording(true);
    } catch (error) {
      console.error('Erro ao iniciar gravação de áudio:', error);
      alert('Erro ao acessar microfone. Verifique as permissões do navegador.');
      setAudioRecording(false);
    }
  };

  const stopAudioRecording = async () => {
    try {
      const mr = mediaRecorderRef.current;
      if (!mr) return;
      if (mr.state === 'inactive') return;
      mr.stop();
      setAudioRecording(false);
      mediaRecorderRef.current = null;
    } catch (error) {
      console.error('Erro ao parar gravação de áudio:', error);
      setAudioRecording(false);
    }
  };

  const handleRemoveAudio = async () => {
    try {
      if (!initialAudioUrl) {
        setInitialAudioEnabled(false);
        return;
      }
      const ref = parseStorageRefFromPublicUrl(initialAudioUrl);
      if (ref) {
        await supabase.storage.from(ref.bucket).remove([ref.path]);
      }
      setInitialAudioUrl('');
      setAudioPreviewUrl('');
      setInitialAudioEnabled(false);
    } catch (error) {
      console.error('Erro ao remover áudio:', error);
      alert('Erro ao remover áudio: ' + error.message);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // Carregar listas do projeto para o dropdown de destino
  useEffect(() => {
    const loadProjectLists = async () => {
      try {
        const { data: lists, error } = await supabase
          .from('project_lists')
          .select('id, name')
          .eq('project_id', projectId)
          .order('name', { ascending: true });

        if (error) throw error;
        setProjectLists(lists || []);
      } catch (error) {
        console.error('Erro ao carregar listas do projeto:', error);
      }
    };

    if (projectId) {
      loadProjectLists();
    }
  }, [projectId]);

  // Carregar regras de follow-up da lista
  useEffect(() => {
    const loadFollowUpRules = async () => {
      if (!list?.id) return;
      
      try {
        setLoadingRules(true);
        const { data: rules, error } = await supabase
          .from('crm_followup_rules')
          .select('*')
          .eq('origin_list_id', list.id)
          .order('order_index', { ascending: true });

        if (error) throw error;
        
        setFollowUpRules(rules || []);
        const hasActiveRules = rules && rules.some(r => r.active);
        setFollowUpEnabled(hasActiveRules || false);
      } catch (error) {
        console.error('Erro ao carregar regras de follow-up:', error);
      } finally {
        setLoadingRules(false);
      }
    };

    loadFollowUpRules();
  }, [list?.id]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (images.length + files.length > MAX_IMAGES) {
      alert(`Máximo de ${MAX_IMAGES} imagens permitidas. Você já tem ${images.length} imagem(ns).`);
      return;
    }

    const newImages = [];
    for (const file of files) {
      if (file.size > MAX_IMAGE_SIZE) {
        alert(`A imagem "${file.name}" deve ter no máximo 3MB`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        alert(`"${file.name}" não é uma imagem válida`);
        continue;
      }

      await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newImages.push(reader.result);
          resolve();
        };
        reader.onerror = () => {
          alert(`Erro ao processar "${file.name}"`);
          reject();
        };
        reader.readAsDataURL(file);
      });
    }

    if (newImages.length > 0) {
      setImages(prevImages => [...prevImages, ...newImages]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Carregar regras de arquivamento da lista
  useEffect(() => {
    const loadArchiveRules = async () => {
      if (!list?.id) return;
      
      try {
        const { data: rules, error } = await supabase
          .from('crm_followup_rules')
          .select('*')
          .eq('origin_list_id', list.id)
          .eq('action_type', 'archive')
          .order('order_index', { ascending: true });

        if (error) throw error;
        
        setArchiveRules(rules || []);
        const hasActiveArchiveRules = rules && rules.some(r => r.active);
        setArchiveEnabled(hasActiveArchiveRules || false);
        
        // Se houver regra ativa, carregar o delay_hours
        if (hasActiveArchiveRules) {
          const activeRule = rules.find(r => r.active);
          if (activeRule) {
            setArchiveDelayHours(activeRule.delay_hours || 48);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar regras de arquivamento:', error);
      }
    };

    loadArchiveRules();
  }, [list?.id]);

  // Criar nova regra de arquivamento
  const handleCreateArchiveRule = async () => {
    try {
      // Validar apenas se for agendado
      if (archiveMode === 'scheduled' && (!archiveDelayHours || archiveDelayHours < 1)) {
        alert('Por favor, defina um número válido de horas');
        return;
      }

      setLoadingRules(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente');
      }

      const url = API_ENDPOINTS.CRM_FOLLOWUP_RULES;
      
      const payload = {
        projectId,
        listId: list.id,
        originListName: list.title,
        delayHours: archiveMode === 'immediate' ? 0 : parseInt(archiveDelayHours),
        actionType: 'archive'
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        throw new Error(`Erro ${response.status}: Resposta inválida do servidor`);
      }

      if (!response.ok) {
        throw new Error(responseData?.error || `Erro ${response.status}: Erro ao criar regra`);
      }

      const rule = responseData.data;
      if (!rule) {
        throw new Error('Regra não foi retornada pelo servidor');
      }
      
      setArchiveRules([...archiveRules, rule]);
      setArchiveEnabled(true);
      setShowArchiveForm(false);
      setArchiveMode('scheduled');
      setArchiveDelayHours(48);
      alert('Regra de arquivamento criada com sucesso!');
    } catch (error) {
      console.error('Erro ao criar regra de arquivamento:', error);
      alert('Erro ao criar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // Deletar regra de arquivamento
  const handleDeleteArchiveRule = async (ruleId) => {
    if (!confirm('Tem certeza que deseja deletar esta regra de arquivamento?')) {
      return;
    }

    try {
      setLoadingRules(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente');
      }

      const url = `${API_ENDPOINTS.CRM_FOLLOWUP_RULES}/${ruleId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.error || `Erro ${response.status}: Erro ao deletar regra`);
      }

      setArchiveRules(archiveRules.filter(r => r.id !== ruleId));
      setArchiveEnabled(false);
      alert('Regra de arquivamento deletada com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar regra de arquivamento:', error);
      alert('Erro ao deletar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // ✅ NOVO: Arquivar todos os contatos da lista
  const handleArchiveAllContacts = async () => {
    if (!confirm(`Tem certeza que deseja arquivar TODOS os contatos da lista "${list?.title}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      setArchivingAll(true);

      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      if (!user?.company_id) {
        throw new Error('Usuário não encontrado. Faça login novamente.');
      }

      // Buscar todos os cards da lista (não arquivados)
      const { data: cards, error: cardsError } = await supabase
        .from('project_items')
        .select('id, metadata')
        .eq('list_id', list.id)
        .eq('is_archived', false);

      if (cardsError) throw cardsError;

      if (!cards || cards.length === 0) {
        alert('Não há contatos ativos nesta lista para arquivar.');
        return;
      }

      const now = new Date().toISOString();
      const listName = list?.title || 'Lista';

      // Arquivar todos os cards com os campos corretos
      const { error: updateError } = await supabase
        .from('project_items')
        .update({
          is_archived: true,
          archived_at: now,
          status: 'archived',
          metadata: {
            archive_reason: 'bulk_archive',
            archive_list_name: listName,
            archived_at: now
          },
          updated_at: now
        })
        .eq('list_id', list.id)
        .eq('is_archived', false);

      if (updateError) throw updateError;

      // Registrar histórico para cada card
      try {
        const historyRecords = cards.map(card => ({
          item_id: card.id,
          company_id: user.company_id,
          action_type: 'archived',
          comment: `Card arquivado em massa na lista "${listName}"`,
          metadata: {
            reason: 'bulk_archive',
            list_name: listName
          },
          created_by: user.id || null
        }));

        await supabase
          .from('item_history')
          .insert(historyRecords);
      } catch (historyError) {
        console.error('Erro ao registrar histórico:', historyError);
        // Não falha o processo se o histórico falhar
      }

      alert(`${cards.length} contato(s) arquivado(s) com sucesso!`);
      
      // Notificar componente pai para atualizar a lista
      if (onSave) {
        onSave({ archived: true, count: cards.length });
      }
    } catch (error) {
      console.error('Erro ao arquivar todos os contatos:', error);
      alert('Erro ao arquivar contatos: ' + error.message);
    } finally {
      setArchivingAll(false);
    }
  };

  // Salvar configuração de mensagem automática
  const handleSaveAutoMessage = async () => {
    try {
      setSavingAutoMessage(true);

      const { error } = await supabase
        .from('project_lists')
        .update({
          automation_rules: {
            auto_message_enabled: autoMessageEnabled,
            auto_message: autoMessage
          },
          metadata: {
            ...(list?.metadata || {}),
            initial_audio_enabled: initialAudioEnabled,
            initial_audio_url: initialAudioUrl || null,
            initial_audio_block_agent_response: blockAgentResponseForInitialAudio
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);

      if (error) throw error;

      alert('Configuração de mensagem automática salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setSavingAutoMessage(false);
    }
  };

  // ✅ NOVO: Adicionar botão de link
  const handleAddButton = () => {
    if (!newButtonLabel.trim() || !newButtonUrl.trim()) {
      alert('Por favor, preencha o rótulo e a URL do botão');
      return;
    }

    try {
      new URL(newButtonUrl);
    } catch (e) {
      alert('URL inválida. Por favor, insira uma URL válida (ex: https://exemplo.com)');
      return;
    }

    if (buttonLinks.length >= MAX_BUTTONS) {
      alert(`Máximo de ${MAX_BUTTONS} botões permitidos`);
      return;
    }

    setButtonLinks([...buttonLinks, { label: newButtonLabel, url: newButtonUrl }]);
    setNewButtonLabel('');
    setNewButtonUrl('');
  };

  // ✅ NOVO: Remover botão de link
  const handleRemoveButton = (index) => {
    setButtonLinks(buttonLinks.filter((_, i) => i !== index));
  };

  // ✅ NOVO: Carregar grupos disponíveis da conexão selecionada
  const handleLoadGroups = async () => {
    if (!selectedConnection?.id) {
      alert('Por favor, selecione uma conexão primeiro');
      return;
    }

    try {
      setLoadingGroups(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      
      const response = await fetch(`${BACKEND_NODE_URL}/connections/groups/${selectedConnection.id}?noparticipants=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token || ''}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao carregar grupos');
      }

      const data = await response.json();
      setAvailableGroups(data.groups || []);
      
      if (!data.groups || data.groups.length === 0) {
        alert('Nenhum grupo encontrado nesta conexão');
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      alert('Erro ao carregar grupos: ' + error.message);
      setAvailableGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // ✅ NOVO: Salvar configuração de notificação em grupo
  const handleSaveGroupNotification = async () => {
    try {
      setSavingGroupNotification(true);

      const { error } = await supabase
        .from('project_lists')
        .update({
          metadata: {
            ...(list?.metadata || {}),
            group_notification: {
              enabled: groupNotificationEnabled,
              connection_id: selectedConnection?.id || null,
              connection_name: selectedConnection?.name || null,
              group_jid: selectedGroup?.jid || null,
              group_name: selectedGroup?.name || null,
              message: groupNotificationMessage
            }
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);

      if (error) throw error;

      alert('Configuração de notificação em grupo salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setSavingGroupNotification(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Salvar configuração de mensagem automática e botões
      await supabase
        .from('project_lists')
        .update({
          automation_rules: {
            auto_message_enabled: autoMessageEnabled,
            auto_message: autoMessage
          },
          metadata: {
            ...(list?.metadata || {}),
            initial_images: images,
            initial_audio_enabled: initialAudioEnabled,
            initial_audio_url: initialAudioUrl || null,
            initial_audio_block_agent_response: blockAgentResponseForInitialAudio,
            button_link_enabled: buttonLinkEnabled,
            button_links: buttonLinks,
            button_link_message: buttonLinkMessage,
            block_agent_response_for_button_link: blockAgentResponseForButtonLink
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);
      
      alert('Configurações salvas com sucesso!');
      onSave(list);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Criar nova regra de follow-up
  const handleCreateFollowUpRule = async () => {
    try {
      if (!newRule.targetListId || !newRule.delayHours) {
        alert('Por favor, preencha todos os campos');
        return;
      }

      const targetList = projectLists.find(l => l.id === newRule.targetListId);
      if (!targetList) {
        alert('Lista de destino não encontrada');
        return;
      }

      setLoadingRules(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente');
      }

      const url = API_ENDPOINTS.CRM_FOLLOWUP_RULES;
      
      const payload = {
        projectId,
        originListId: list.id,
        originListName: list.title,
        targetListId: newRule.targetListId,
        targetListName: targetList.name,
        delayHours: parseInt(newRule.delayHours)
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        throw new Error(`Erro ${response.status}: Resposta inválida do servidor`);
      }

      if (!response.ok) {
        throw new Error(responseData?.error || `Erro ${response.status}: Erro ao criar regra`);
      }

      const rule = responseData.data;
      if (!rule) {
        throw new Error('Regra não foi retornada pelo servidor');
      }
      
      setFollowUpRules([...followUpRules, rule]);
      setNewRule({ targetListId: '', delayHours: 24 });
      setShowFollowUpForm(false);
      alert('Regra de follow-up criada com sucesso!');
    } catch (error) {
      console.error('Erro ao criar regra:', error);
      alert('Erro ao criar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // Deletar regra de follow-up
  const handleDeleteFollowUpRule = async (ruleId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta regra?')) return;

    try {
      setLoadingRules(true);

      const response = await fetch(`${API_ENDPOINTS.CRM_FOLLOWUP_RULES}/${ruleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ projectId })
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar regra');
      }

      setFollowUpRules(followUpRules.filter(r => r.id !== ruleId));
      alert('Regra deletada com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar regra:', error);
      alert('Erro ao deletar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // Ativar/Desativar regra de follow-up
  const handleToggleFollowUpRule = async (rule) => {
    try {
      setLoadingRules(true);

      const response = await fetch(`${API_ENDPOINTS.CRM_FOLLOWUP_RULES}/${rule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          projectId,
          active: !rule.active
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar regra');
      }

      const { data: updatedRule } = await response.json();
      setFollowUpRules(followUpRules.map(r => r.id === rule.id ? updatedRule : r));
    } catch (error) {
      console.error('Erro ao atualizar regra:', error);
      alert('Erro ao atualizar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // ✅ NOVO: Carregar configuração de encaminhamento
  const loadForwardConfig = async () => {
    if (!list?.id || !projectId) return;
    try {
      setLoadingForward(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      // Carregar configuração de encaminhamento
      const response = await fetch(
        `${API_ENDPOINTS.LEAD_FORWARD}/lists/${list.id}/forward`,
        {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        }
      );

      if (response.ok) {
        const { data } = await response.json();
        console.log('[Forward] Config carregada:', data);
        if (data) {
          setForwardEnabled(data.is_enabled || false);
          setForwardTargetProjectId(data.target_project_id || '');
          setForwardTargetListId(data.target_list_id || '');
          setForwardMode(data.delay_hours ? 'scheduled' : 'immediate');
          setForwardDelayHours(data.delay_hours || 1);
          setForwardConfig(data);
          
          // Se tem projeto selecionado, carregar as listas (preservando a lista já salva)
          if (data.target_project_id) {
            console.log('[Forward] Carregando listas do projeto:', data.target_project_id);
            await handleProjectChange(data.target_project_id, true);
          }
        }
      }

      // Carregar projetos disponíveis
      console.log('[Forward] Carregando projetos...');
      const projectsResponse = await fetch(
        `${API_ENDPOINTS.LEAD_FORWARD}/projects`,
        {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        }
      );

      console.log('[Forward] Status da resposta:', projectsResponse.status);
      
      if (projectsResponse.ok) {
        const { data: projects } = await projectsResponse.json();
        console.log('[Forward] Projetos recebidos:', projects);
        setAvailableProjects(projects || []);
      } else {
        const errorText = await projectsResponse.text();
        console.error('[Forward] Erro ao carregar projetos:', projectsResponse.status, errorText);
      }
    } catch (error) {
      console.error('Erro ao carregar configuração de encaminhamento:', error);
    } finally {
      setLoadingForward(false);
    }
  };

  // ✅ NOVO: Carregar listas do projeto selecionado
  const handleProjectChange = async (projectId, preserveListId = false) => {
    setForwardTargetProjectId(projectId);
    if (!preserveListId) {
      setForwardTargetListId('');
    }
    
    if (!projectId) {
      setAvailableLists([]);
      return;
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(
        `${API_ENDPOINTS.LEAD_FORWARD}/projects/${projectId}/lists`,
        {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        }
      );

      if (response.ok) {
        const { data } = await response.json();
        setAvailableLists(data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar listas do projeto:', error);
    }
  };

  // ✅ NOVO: Salvar configuração de encaminhamento
  const saveForwardConfig = async () => {
    try {
      setLoadingForward(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Sessão expirada');

      console.log('[Forward] Salvando config:', {
        forwardTargetProjectId,
        forwardTargetListId,
        forwardEnabled,
        forwardMode,
        forwardDelayHours
      });

      const response = await fetch(
        `${API_ENDPOINTS.LEAD_FORWARD}/lists/${list.id}/forward`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            project_id: projectId,
            is_enabled: forwardEnabled,
            target_project_id: forwardTargetProjectId,
            target_list_id: forwardTargetListId,
            delay_hours: forwardMode === 'scheduled' ? forwardDelayHours : null
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao salvar configuração');
      }

      const { data } = await response.json();
      console.log('[Forward] Config salva:', data);
      setForwardConfig(data);
      return data;
    } catch (error) {
      console.error('Erro ao salvar encaminhamento:', error);
      alert('Erro ao salvar configuração de encaminhamento: ' + error.message);
      throw error;
    } finally {
      setLoadingForward(false);
    }
  };

  // ✅ Salvar nome, cor e ícone da lista
  const handleSaveListInfo = async () => {
    if (!listName.trim()) return;
    try {
      setSavingListInfo(true);
      const { data, error } = await supabase
        .from('project_lists')
        .update({ name: listName.trim(), color: listColor, icon: listIcon })
        .eq('id', list.id)
        .select('id, name, color, icon')
        .single();
      
      if (error) throw error;
      
      alert('✅ Informações da lista salvas com sucesso!');
      if (onSave) onSave({ ...list, title: data.name, color: data.color, icon: data.icon });
    } catch (err) {
      console.error('❌ Erro ao salvar info da lista:', err);
      alert('❌ Erro ao salvar: ' + err.message);
    } finally {
      setSavingListInfo(false);
    }
  };

  // Salvar lista de remarketing
  const handleSaveRemarketingList = async () => {
    if (!remarketingListName.trim()) {
      alert('Por favor, preencha o nome da lista de remarketing');
      return;
    }

    try {
      setSavingRemarketing(true);
      const { data: session } = await supabase.auth.getSession();

      if (!session?.session?.access_token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      // 1. Buscar TODOS os cards NÃO ARQUIVADOS da lista do CRM
      console.log('[Remarketing] Buscando cards da lista do CRM:', list.id);
      const { data: items, error: itemsError } = await supabase
        .from('project_items')
        .select('id, entity_type, entity_id, title')
        .eq('list_id', list.id)
        .eq('is_archived', false);

      if (itemsError) {
        throw new Error(`Erro ao buscar cards: ${itemsError.message}`);
      }

      if (!items || items.length === 0) {
        throw new Error('Nenhum card ativo encontrado nesta lista do CRM');
      }

      // 2. Extrair IDs dos contatos (entity_id onde entity_type === 'contact')
      const selectedContactIds = items
        .filter(item => item.entity_type === 'contact')
        .map(item => item.entity_id);

      console.log('[Remarketing] Contatos encontrados:', {
        total_cards_ativos: items.length,
        total_contatos: selectedContactIds.length,
        primeiros_5: selectedContactIds.slice(0, 5)
      });

      if (selectedContactIds.length === 0) {
        throw new Error('Nenhum contato ativo encontrado nesta lista do CRM');
      }

      // 3. Enviar para o backend
      const endpoint = `${BACKEND_NODE_URL}/api/marketing-campaigns/save-contact-list`;

      console.log('[Remarketing] Enviando requisição para o backend...', {
        total_contatos: selectedContactIds.length,
        endpoint: endpoint
      });

      // Criar AbortController com timeout de 120 segundos (para listas grandes)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.session.access_token}`
          },
          body: JSON.stringify({
            projectId: projectId,
            listId: list.id,
            listName: remarketingListName.trim(),
            selectedContactIds: selectedContactIds,
            connectionId: connection?.id || null
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[Remarketing] ❌ Resposta do servidor:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText.substring(0, 200)
          });
          
          try {
            const error = JSON.parse(errorText);
            throw new Error(error.error || `Erro ${response.status}: ${error.details || errorText}`);
          } catch (e) {
            throw new Error(`Erro ${response.status}: ${errorText.substring(0, 100)}`);
          }
        }

        const data = await response.json();
        console.log('[Remarketing] ✅ Lista criada com sucesso:', data);
        alert(`✅ Lista de campanha criada com sucesso!\n\nContatos: ${data.data.total_contacts}\nCom telefone: ${data.data.contacts_with_phone}`);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Timeout: A requisição demorou muito. Tente com uma lista menor.');
        }
        throw fetchError;
      }
      
      // Resetar formulário
      setRemarketingListName('');
      setRemarketingClearAfterSave(false);
      setRemarketingAutoEnabled(false);
      setRemarketingTimerHours(24);

    } catch (error) {
      console.error('Erro ao salvar lista de campanha:', error);
      alert(`❌ Erro ao salvar: ${error.message}`);
    } finally {
      setSavingRemarketing(false);
    }
  };

  // Carregar configuração de encaminhamento ao montar
  useEffect(() => {
    const loadConfig = async () => {
      if (!list?.id || !projectId) return;
      try {
        setLoadingForward(true);
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;

        const response = await fetch(
          `${API_ENDPOINTS.LEAD_FORWARD}/lists/${list.id}/forward`,
          {
            headers: { 'Authorization': `Bearer ${session.session.access_token}` }
          }
        );

        if (response.ok) {
          const { data } = await response.json();
          setForwardConfig(data);
          if (data) {
            setForwardEnabled(data.is_enabled || false);
            setForwardTargetProjectId(data.target_project_id || '');
            setForwardTargetListId(data.target_list_id || '');
            setForwardMode(data.delay_hours ? 'scheduled' : 'immediate');
            setForwardDelayHours(data.delay_hours || 1);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar config de encaminhamento:', error);
      } finally {
        setLoadingForward(false);
      }
    };

    loadConfig();
  }, [list?.id, projectId]);


  return (
    <div className="modal-overlay-crm-followup-list" onClick={onClose}>
      <div className="crm-followup-list-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="crm-modal-header">
          <div className="crm-modal-title-section">
            <AppIcon name="replay" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="crm-modal-content">
          {/* Seção Principal */}
          <div className="crm-modal-main">

            {/* ========== SEÇÃO 0: INFORMAÇÕES DA LISTA ========== */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="edit" />
                  <h4>Informações da Lista</h4>
                </div>
              </div>

              {/* Nome da lista */}
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.25rem', display: 'block' }}>Nome da lista</label>
                <input
                  type="text"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder="Nome da lista"
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    color: '#e2e8f0',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Cor da lista */}
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.25rem', display: 'block' }}>Cor</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {LIST_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => setListColor(color)}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: color,
                        border: listColor === color ? '2px solid #fff' : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'transform 0.15s',
                        transform: listColor === color ? 'scale(1.15)' : 'scale(1)'
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Ícone da lista */}
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.25rem', display: 'block' }}>Ícone</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {LIST_ICONS.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setListIcon(icon)}
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '6px',
                        background: listIcon === icon ? 'rgba(180,59,234,0.3)' : 'rgba(255,255,255,0.05)',
                        border: listIcon === icon ? '2px solid #b43bea' : '1px solid rgba(255,255,255,0.08)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s'
                      }}
                    >
                      <AppIcon name={icon} style={{ fontSize: '18px', color: listIcon === icon ? '#b43bea' : '#94a3b8' }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Botão salvar */}
              <button
                onClick={handleSaveListInfo}
                disabled={savingListInfo || !listName.trim()}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #b43bea, #8b5cf6)',
                  color: '#fff',
                  border: 'none',
                  cursor: savingListInfo ? 'wait' : 'pointer',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  opacity: savingListInfo || !listName.trim() ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <AppIcon name="save" style={{ fontSize: '18px' }} />
                {savingListInfo ? 'Salvando...' : 'Salvar informações'}
              </button>
            </div>
            
            {/* ========== SEÇÃO 1: MENSAGEM AUTOMÁTICA ========== */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="chat" />
                  <h4>Mensagem Automática</h4>
                </div>
              </div>

              {/* Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={autoMessageEnabled}
                    onChange={(e) => setAutoMessageEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {autoMessageEnabled 
                      ? '✅ Mensagem automática ativa' 
                      : '❌ Mensagem automática inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {autoMessageEnabled 
                    ? 'Uma mensagem será enviada automaticamente quando um contato entrar nesta lista'
                    : 'Nenhuma mensagem será enviada automaticamente'}
                </small>
              </div>

              {/* Configuração de Mensagem Fixa */}
              {autoMessageEnabled && (
                <div className="auto-message-config">
                  <div className="form-group">
                    <label>Mensagem fixa a ser enviada:</label>
                    <textarea
                      value={autoMessage}
                      onChange={(e) => setAutoMessage(e.target.value)}
                      placeholder="Digite a mensagem que será enviada automaticamente quando um contato entrar nesta lista..."
                      rows={5}
                      className="auto-message-textarea"
                    />
                  </div>

                  {/* Preview da Mensagem */}
                  {autoMessage && (
                    <div className="message-preview">
                      <label>Preview:</label>
                      <div className="preview-box">
                        {autoMessage
                          .replace(/\{\{nome\}\}/gi, 'João Silva')
                          .replace(/\{\{primeiro_nome\}\}/gi, 'João')
                          .replace(/\{\{telefone\}\}/gi, '11999999999')
                          .replace(/\{\{empresa\}\}/gi, 'Sua Empresa')}
                      </div>
                    </div>
                  )}

                  <button
                    className="btn-save-auto-message"
                    onClick={handleSaveAutoMessage}
                    disabled={savingAutoMessage}
                  >
                    <AppIcon name="save" />
                    {savingAutoMessage ? 'Salvando...' : 'Salvar Configuração'}
                  </button>
                </div>
              )}

              <small className="section-description">
                Configure uma mensagem que será enviada automaticamente via WhatsApp quando um contato for movido para esta lista (por automação ou manualmente).
              </small>
            </div>

            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="image" />
                  <h4>Enviar Imagem Automática ({images.length}/{MAX_IMAGES})</h4>
                </div>
              </div>

              <div className="image-upload-container">
                <div className="upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="followup-image-input"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={images.length >= MAX_IMAGES}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="followup-image-input"
                    className="upload-label"
                    style={{ opacity: images.length >= MAX_IMAGES ? 0.5 : 1, cursor: images.length >= MAX_IMAGES ? 'not-allowed' : 'pointer' }}
                  >
                    <AppIcon name="cloud_upload" />
                    <span>{images.length >= MAX_IMAGES ? 'Limite de imagens atingido' : 'Clique para selecionar imagens'}</span>
                    <small>ou arraste e solte aqui</small>
                  </label>
                </div>

                {images.length > 0 && (
                  <div className="images-preview-container">
                    <div className="preview-label">Previews ({images.length}):</div>
                    <div className="images-grid">
                      {images.map((image, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={image} alt={`preview-${index}`} />
                          <div className="image-number">{index + 1}</div>
                          <button
                            className="btn-remove-image-small"
                            onClick={() => handleRemoveImage(index)}
                            type="button"
                            title="Remover imagem"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="image-info">
                <p>
                  <strong>Formatos suportados:</strong> JPG, PNG, GIF, WebP
                </p>
                <p>
                  <strong>Tamanho máximo por imagem:</strong> 3MB
                </p>
                <p>
                  <strong>Máximo de imagens:</strong> {MAX_IMAGES}
                </p>
                <p>
                  As imagens serão enviadas automaticamente quando um contato entrar nesta lista.
                </p>
              </div>
            </div>

            {/* ========== SEÇÃO 2: FOLLOW-UP AUTOMÁTICO ========== */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="schedule" />
                  <h4>Follow-up Automático</h4>
                </div>
              </div>

              {/* Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={followUpEnabled}
                    onChange={(e) => setFollowUpEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {followUpEnabled 
                      ? '✅ Follow-up ativo' 
                      : '❌ Follow-up inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {followUpEnabled 
                    ? 'Leads serão movidos automaticamente conforme as regras configuradas'
                    : 'Nenhum lead será movido automaticamente'}
                </small>
              </div>

              {/* Lista de Regras Existentes */}
              {followUpRules.length > 0 && (
                <div className="followup-rules-container">
                  <div className="followup-rules-label">Regras Configuradas ({followUpRules.length}):</div>
                  <div className="followup-rules-list">
                    {followUpRules.map((rule, index) => (
                      <div key={rule.id} className="followup-rule-item">
                        <div className="rule-info">
                          <div className="rule-number">Regra {index + 1}</div>
                          <div className="rule-details">
                            <span className="rule-arrow">→</span>
                            <span className="rule-target">{rule.target_list_name}</span>
                            <span className="rule-delay">após {rule.delay_hours}h</span>
                          </div>
                        </div>
                        <div className="rule-actions">
                          <button
                            className={`btn-toggle-rule ${rule.active ? 'active' : 'inactive'}`}
                            onClick={() => handleToggleFollowUpRule(rule)}
                            disabled={loadingRules}
                            title={rule.active ? 'Desativar' : 'Ativar'}
                          >
                            <AppIcon name={rule.active ? 'check_circle' : 'radio_button_unchecked'} />
                          </button>
                          <button
                            className="btn-delete-rule"
                            onClick={() => handleDeleteFollowUpRule(rule.id)}
                            disabled={loadingRules}
                            title="Deletar"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulário para Criar Nova Regra */}
              {!showFollowUpForm ? (
                <button
                  className="btn-add-followup-rule"
                  onClick={() => setShowFollowUpForm(true)}
                  disabled={loadingRules}
                >
                  <AppIcon name="add" />
                  Adicionar Regra de Follow-up
                </button>
              ) : (
                <div className="followup-form">
                  <div className="form-group">
                    <label>Quantas horas para encaminhar?</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Ex: 1, 2, 24, 48"
                      value={newRule.delayHours}
                      onChange={(e) => setNewRule({ ...newRule, delayHours: e.target.value })}
                      disabled={loadingRules}
                      className="delay-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Encaminhar para</label>
                    <select
                      value={newRule.targetListId}
                      onChange={(e) => setNewRule({ ...newRule, targetListId: e.target.value })}
                      disabled={loadingRules}
                    >
                      <option value="">Selecione uma lista...</option>
                      {projectLists
                        .filter(l => l.id !== list.id)
                        .map(l => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn-cancel-form"
                      onClick={() => setShowFollowUpForm(false)}
                      disabled={loadingRules}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn-create-rule"
                      onClick={handleCreateFollowUpRule}
                      disabled={loadingRules || !newRule.targetListId || !newRule.delayHours}
                    >
                      {loadingRules ? 'Salvando...' : 'Salvar Configuração'}
                    </button>
                  </div>
                </div>
              )}

              <small className="followup-description">
                Quando habilitado, leads que permanecerem nesta lista por mais de N horas serão automaticamente movidos para a lista de destino.
              </small>
            </div>

            {/* ✅ NOVO: Seção de Botão de Link */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="link" />
                  <h4>Enviar Botões de Link</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={buttonLinkEnabled}
                    onChange={(e) => setButtonLinkEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {buttonLinkEnabled ? '✅ Botões ativos' : '❌ Botões inativos'}
                  </span>
                </label>
                <small className="toggle-description">
                  {buttonLinkEnabled 
                    ? 'Botões de link serão enviados para contatos que entram nesta lista'
                    : 'Nenhum botão será enviado'}
                </small>
              </div>

              {buttonLinkEnabled && (
                <div className="auto-message-config">
                  <div className="form-group">
                    <label>Mensagem antes dos botões:</label>
                    <textarea
                      value={buttonLinkMessage}
                      onChange={(e) => setButtonLinkMessage(e.target.value)}
                      placeholder="Digite a mensagem que será enviada antes dos botões..."
                      rows={2}
                      className="auto-message-textarea"
                    />
                  </div>

                  <div className="button-form">
                    <div className="form-group">
                      <label>Rótulo do botão:</label>
                      <input
                        type="text"
                        value={newButtonLabel}
                        onChange={(e) => setNewButtonLabel(e.target.value)}
                        placeholder="Ex: Visitar Site"
                        maxLength={20}
                      />
                    </div>

                    <div className="form-group">
                      <label>URL do botão:</label>
                      <input
                        type="url"
                        value={newButtonUrl}
                        onChange={(e) => setNewButtonUrl(e.target.value)}
                        placeholder="Ex: https://exemplo.com"
                      />
                    </div>

                    <button
                      type="button"
                      className="btn-add-followup-rule"
                      onClick={handleAddButton}
                      disabled={buttonLinks.length >= MAX_BUTTONS}
                    >
                      <AppIcon name="add" />
                      Adicionar Botão ({buttonLinks.length}/{MAX_BUTTONS})
                    </button>
                  </div>

                  {buttonLinks.length > 0 && (
                    <div className="buttons-list">
                      <div className="buttons-label">Botões Configurados ({buttonLinks.length}):</div>
                      <div className="buttons-container">
                        {buttonLinks.map((button, index) => (
                          <div key={index} className="button-item">
                            <div className="button-info">
                              <div className="button-label">{button.label}</div>
                              <div className="button-url">{button.url}</div>
                            </div>
                            <button
                              className="btn-delete-rule"
                              onClick={() => handleRemoveButton(index)}
                              type="button"
                              title="Remover botão"
                            >
                              <AppIcon name="delete" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {buttonLinks.length > 0 && (
                    <div className="message-preview">
                      <label>Preview:</label>
                      <div className="preview-box">
                        <div style={{ marginBottom: '0.5rem', color: '#cbd5e0' }}>
                          {buttonLinkMessage}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {buttonLinks.map((button, index) => (
                            <button
                              key={index}
                              style={{
                                padding: '0.75rem',
                                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                border: '1px solid rgba(16, 185, 129, 0.5)',
                                borderRadius: '8px',
                                color: '#10b981',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500'
                              }}
                              disabled
                            >
                              {button.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ✅ NOVO: Opção de bloquear resposta do agente IA */}
                  <div className="tool-enable-section">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={blockAgentResponseForButtonLink}
                        onChange={(e) => setBlockAgentResponseForButtonLink(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        {blockAgentResponseForButtonLink 
                          ? '✅ Bloquear resposta do agente IA' 
                          : '❌ Permitir resposta do agente IA'}
                      </span>
                    </label>
                    <small className="toggle-description">
                      {blockAgentResponseForButtonLink 
                        ? 'O agente de IA não responderá quando os botões forem enviados, evitando duplicação de mensagens'
                        : 'O agente de IA poderá responder normalmente quando os botões forem enviados'}
                    </small>
                  </div>

                  <div className="info-box">
                    <AppIcon name="info" />
                    <p>
                      Os botões serão enviados via WhatsApp como menu interativo. Máximo de {MAX_BUTTONS} botões por mensagem.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ✅ NOVO: Seção de Arquivamento Automático */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="archive" />
                  <h4>Arquivamento Automático</h4>
                </div>
              </div>

              {/* Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={archiveEnabled}
                    onChange={(e) => setArchiveEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {archiveEnabled 
                      ? '✅ Arquivamento ativo' 
                      : '❌ Arquivamento inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {archiveEnabled 
                    ? 'Cards serão removidos do Kanban após X horas'
                    : 'Nenhum card será arquivado automaticamente'}
                </small>
              </div>

              {/* Lista de Regras Existentes */}
              {archiveRules.length > 0 && (
                <div className="followup-rules-container">
                  <div className="followup-rules-label">Regras Configuradas ({archiveRules.length}):</div>
                  <div className="followup-rules-list">
                    {archiveRules.map((rule) => (
                      <div key={rule.id} className="followup-rule-item">
                        <div className="rule-info">
                          <div className="rule-number">
                            {rule.delay_hours === 0 
                              ? 'Arquivar imediatamente (ao entrar na lista)' 
                              : `Arquivar após ${rule.delay_hours}h`}
                          </div>
                          <div className="rule-details">
                            <span className="rule-target">
                              {rule.active ? '✅ Ativa' : '⭕ Inativa'}
                            </span>
                          </div>
                        </div>
                        <div className="rule-actions">
                          <button
                            className={`btn-toggle-rule ${rule.active ? 'active' : 'inactive'}`}
                            onClick={() => {
                              // Toggle rule active status
                              setArchiveRules(archiveRules.map(r => 
                                r.id === rule.id ? { ...r, active: !r.active } : r
                              ));
                            }}
                            disabled={loadingRules}
                            title={rule.active ? 'Desativar' : 'Ativar'}
                          >
                            <AppIcon name={rule.active ? 'check_circle' : 'radio_button_unchecked'} />
                          </button>
                          <button
                            className="btn-delete-rule"
                            onClick={() => handleDeleteArchiveRule(rule.id)}
                            disabled={loadingRules}
                            title="Deletar"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulário para Criar Nova Regra */}
              {!showArchiveForm ? (
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn-add-followup-rule"
                    onClick={() => setShowArchiveForm(true)}
                    disabled={loadingRules}
                  >
                    <AppIcon name="add" />
                    Adicionar Regra de Arquivamento
                  </button>
                  <button
                    className="btn-delete-rule"
                    onClick={handleArchiveAllContacts}
                    disabled={archivingAll || loadingRules}
                    style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', borderColor: 'rgba(239, 68, 68, 0.5)' }}
                  >
                    <AppIcon name="archive" />
                    {archivingAll ? 'Arquivando...' : 'Arquivar Todos Agora'}
                  </button>
                </div>
              ) : (
                <div className="followup-form">
                  <div className="form-group">
                    <label>Modo de arquivamento:</label>
                    <div className="radio-group" style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                      <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="archiveMode"
                          value="immediate"
                          checked={archiveMode === 'immediate'}
                          onChange={() => setArchiveMode('immediate')}
                          disabled={loadingRules}
                        />
                        <span>Imediato (ao entrar na lista)</span>
                      </label>
                      <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="archiveMode"
                          value="scheduled"
                          checked={archiveMode === 'scheduled'}
                          onChange={() => setArchiveMode('scheduled')}
                          disabled={loadingRules}
                        />
                        <span>Agendado (após X horas)</span>
                      </label>
                    </div>
                  </div>

                  {archiveMode === 'scheduled' && (
                    <div className="form-group">
                      <label>Quantas horas para arquivar?</label>
                      <input
                        type="number"
                        min="1"
                        max="720"
                        placeholder="Ex: 24, 48, 72"
                        value={archiveDelayHours}
                        onChange={(e) => setArchiveDelayHours(parseInt(e.target.value) || 1)}
                        disabled={loadingRules}
                        className="delay-input"
                      />
                    </div>
                  )}

                  <div className="form-actions">
                    <button
                      className="btn-cancel-form"
                      onClick={() => {
                        setShowArchiveForm(false);
                        setArchiveMode('scheduled');
                        setArchiveDelayHours(48);
                      }}
                      disabled={loadingRules}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn-create-rule"
                      onClick={handleCreateArchiveRule}
                      disabled={loadingRules || (archiveMode === 'scheduled' && !archiveDelayHours)}
                    >
                      {loadingRules ? 'Salvando...' : 'Salvar Configuração'}
                    </button>
                  </div>
                </div>
              )}

              <small className="section-description">
                Configure o arquivamento automático para remover cards do Kanban. Cards arquivados não aparecem mais nas listas.
              </small>
            </div>

            {/* ✅ NOVO: Seção de Encaminhar para Projeto */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="forward" />
                  <h4>Encaminhar para Projeto</h4>
                </div>
              </div>

              {/* Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={forwardEnabled}
                    onChange={(e) => setForwardEnabled(e.target.checked)}
                    disabled={loadingForward}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {forwardEnabled
                      ? '✅ Encaminhamento ativo'
                      : '❌ Encaminhamento inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {forwardEnabled
                    ? 'Cards serão encaminhados automaticamente para outro projeto/lista'
                    : 'Nenhum card será encaminhado automaticamente'}
                </small>
              </div>

              {/* Configuração de Encaminhamento */}
              {forwardEnabled && (
                <div className="auto-message-config">
                  {/* Seleção do Projeto de Destino */}
                  <div className="form-group">
                    <label>Projeto de destino:</label>
                    <select
                      value={forwardTargetProjectId}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      disabled={loadingForward}
                    >
                      <option value="">Selecione um projeto...</option>
                      {availableProjects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                    {availableProjects.length === 0 && !loadingForward && (
                      <small className="field-hint" style={{ color: '#f59e0b' }}>
                        Nenhum outro projeto CRM encontrado na sua empresa
                      </small>
                    )}
                  </div>

                  {/* Seleção da Lista de Destino */}
                  {forwardTargetProjectId && (
                    <div className="form-group">
                      <label>Lista de destino:</label>
                      <select
                        value={forwardTargetListId}
                        onChange={(e) => setForwardTargetListId(e.target.value)}
                        disabled={loadingForward}
                      >
                        <option value="">Selecione uma lista...</option>
                        {availableLists.map((listItem) => (
                          <option key={listItem.id} value={listItem.id}>
                            {listItem.name}
                          </option>
                        ))}
                      </select>
                      {availableLists.length === 0 && !loadingForward && (
                        <small className="field-hint" style={{ color: '#f59e0b' }}>
                          Nenhuma lista encontrada neste projeto
                        </small>
                      )}
                    </div>
                  )}

                  {/* Modo de Encaminhamento */}
                  <div className="form-group">
                    <label>Quando encaminhar:</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="forwardMode"
                          value="immediate"
                          checked={forwardMode === 'immediate'}
                          onChange={(e) => setForwardMode(e.target.value)}
                          disabled={loadingForward}
                        />
                        <span>Imediatamente ao entrar na lista</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="forwardMode"
                          value="scheduled"
                          checked={forwardMode === 'scheduled'}
                          onChange={(e) => setForwardMode(e.target.value)}
                          disabled={loadingForward}
                        />
                        <span>Após um período de tempo</span>
                      </label>
                    </div>
                  </div>

                  {/* Delay Hours (se agendado) */}
                  {forwardMode === 'scheduled' && (
                    <div className="form-group">
                      <label>Horas de espera:</label>
                      <input
                        type="number"
                        min="1"
                        max="720"
                        value={forwardDelayHours}
                        onChange={(e) => setForwardDelayHours(parseInt(e.target.value) || 1)}
                        disabled={loadingForward}
                        className="delay-input"
                      />
                      <small className="field-hint">
                        O card será encaminhado após {forwardDelayHours} hora(s) na lista
                      </small>
                    </div>
                  )}

                  {/* Info sobre configuração */}
                  {forwardConfig && (
                    <div className="forward-info">
                      <AppIcon name="info" />
                      <p>
                        Configuração salva em: {new Date(forwardConfig.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  )}

                  {/* Botão Salvar */}
                  <button
                    className="btn-save-auto-message"
                    onClick={saveForwardConfig}
                    disabled={loadingForward || !forwardTargetProjectId || !forwardTargetListId}
                  >
                    <AppIcon name="save" />
                    {loadingForward ? 'Salvando...' : 'Salvar Configuração'}
                  </button>
                </div>
              )}

              <small className="section-description">
                Configure o encaminhamento automático para mover cards para outro projeto e lista quando entrarem nesta lista.
              </small>
            </div>

            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="mic" />
                  <h4>Enviar áudio</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={initialAudioEnabled}
                    onChange={(e) => {
                      const next = e.target.checked;
                      if (next && !initialAudioUrl) {
                        alert('Grave e salve um áudio antes de habilitar.');
                        return;
                      }
                      setInitialAudioEnabled(next);
                    }}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {initialAudioEnabled ? '✅ Áudio ativo' : '❌ Áudio inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {initialAudioUrl
                    ? 'Um áudio será enviado automaticamente quando um contato entrar nesta lista'
                    : 'Grave um áudio para habilitar esta ferramenta'}
                </small>
              </div>

              <div className="auto-message-config">
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-add-followup-rule"
                      onClick={startAudioRecording}
                      disabled={audioUploading || audioRecording}
                    >
                      {audioRecording ? 'Gravando...' : 'Gravar áudio'}
                    </button>

                    <button
                      type="button"
                      className="btn-cancel-form"
                      onClick={stopAudioRecording}
                      disabled={audioUploading || !audioRecording}
                    >
                      Parar
                    </button>

                    <button
                      type="button"
                      className="btn-delete-rule"
                      onClick={handleRemoveAudio}
                      disabled={audioUploading || (!initialAudioUrl && audioRecording)}
                    >
                      Remover áudio
                    </button>
                  </div>
                </div>

                {audioUploading && (
                  <small className="toggle-description">Salvando áudio...</small>
                )}

                {(initialAudioUrl || audioPreviewUrl) && (
                  <div className="message-preview">
                    <label>Preview:</label>
                    <div className="preview-box">
                      <audio
                        controls
                        src={audioPreviewUrl || initialAudioUrl}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                )}

                <div className="block-agent-section">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={blockAgentResponseForInitialAudio}
                      onChange={(e) => setBlockAgentResponseForInitialAudio(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      Bloquear resposta do agente IA
                    </span>
                  </label>
                  <small className="toggle-description">
                    {blockAgentResponseForInitialAudio
                      ? '⚠️ O agente NÃO responderá quando este áudio for enviado.'
                      : '✅ O agente responderá normalmente após o áudio ser enviado.'}
                  </small>
                </div>
              </div>

              <small className="section-description">
                Configure um áudio que será enviado automaticamente via WhatsApp quando um contato for movido para esta lista.
              </small>
            </div>

            {/* ✅ NOVO: Seção de Notificação em Grupo WhatsApp */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="group" />
                  <h4>Notificação em Grupo WhatsApp</h4>
                </div>
              </div>

              {/* Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={groupNotificationEnabled}
                    onChange={(e) => setGroupNotificationEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {groupNotificationEnabled 
                      ? '✅ Notificação ativa' 
                      : '❌ Notificação inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {groupNotificationEnabled 
                    ? 'Uma notificação será enviada para o grupo quando um contato entrar nesta lista'
                    : 'Nenhuma notificação será enviada para o grupo'}
                </small>
              </div>

              {/* Configuração de Notificação em Grupo */}
              {groupNotificationEnabled && (
                <div className="auto-message-config">
                  {/* Seletor de Conexão */}
                  <div className="form-group">
                    <label>Selecione a conexão WhatsApp:</label>
                    <select
                      value={selectedConnection?.id || ''}
                      onChange={(e) => {
                        const connId = e.target.value;
                        const conn = availableConnections.find(c => c.id === connId);
                        setSelectedConnection(conn || null);
                        setAvailableGroups([]);
                        setSelectedGroup(null);
                      }}
                      disabled={loadingConnections || availableConnections.length === 0}
                    >
                      <option value="">Selecione uma conexão...</option>
                      {availableConnections.map((conn) => (
                        <option key={conn.id} value={conn.id}>
                          {conn.name} ({conn.number})
                        </option>
                      ))}
                    </select>
                    {availableConnections.length === 0 && !loadingConnections && (
                      <small className="field-hint" style={{ color: '#f59e0b' }}>
                        Nenhuma conexão configurada no projeto
                      </small>
                    )}
                  </div>

                  {/* Botão para carregar grupos */}
                  {selectedConnection && (
                    <div className="form-group">
                      <button
                        type="button"
                        className="btn-add-followup-rule"
                        onClick={handleLoadGroups}
                        disabled={loadingGroups}
                      >
                        {loadingGroups ? 'Carregando grupos...' : 'Carregar Grupos'}
                      </button>
                    </div>
                  )}

                  {/* Lista de grupos disponíveis */}
                  {availableGroups.length > 0 && (
                    <div className="form-group">
                      <label style={{ fontSize: '0.9rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                        Selecione um grupo:
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                        {availableGroups.map((group) => (
                          <div
                            key={group.jid}
                            onClick={() => setSelectedGroup({ jid: group.jid, name: group.name })}
                            style={{
                              padding: '0.75rem',
                              borderRadius: '6px',
                              border: `1px solid ${selectedGroup?.jid === group.jid ? 'var(--accent-color)' : 'var(--border-color)'}`,
                              background: selectedGroup?.jid === group.jid ? 'rgba(6, 182, 212, 0.1)' : 'var(--card-background)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <AppIcon name={selectedGroup?.jid === group.jid ? 'check_circle' : 'radio_button_unchecked'} style={{ fontSize: '1.2rem', color: selectedGroup?.jid === group.jid ? 'var(--accent-color)' : 'var(--text-color-secondary)' }} />
                            <span style={{ flex: 1 }}>{group.name}</span>
                            {group.participant_count && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)' }}>
                                {group.participant_count} participantes
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {availableGroups.length === 0 && !loadingGroups && selectedConnection && (
                    <div className="info-box">
                      <AppIcon name="info" />
                      <p>
                        Clique em "Carregar Grupos" para buscar os grupos disponíveis nesta conexão.
                      </p>
                    </div>
                  )}

                  {/* Grupo selecionado */}
                  {selectedGroup && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(6, 182, 212, 0.1)', borderRadius: '6px', border: '1px solid rgba(6, 182, 212, 0.3)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AppIcon name="check_circle" style={{ color: 'var(--accent-color)' }} />
                        <span style={{ fontWeight: 500 }}>Grupo selecionado: {selectedGroup.name}</span>
                      </div>
                    </div>
                  )}

                  {/* Campo de mensagem */}
                  {selectedGroup && (
                    <div className="form-group">
                      <label>Mensagem fixa a ser enviada no grupo:</label>
                      <textarea
                        value={groupNotificationMessage}
                        onChange={(e) => setGroupNotificationMessage(e.target.value)}
                        placeholder="Digite a mensagem que será enviada no grupo quando um contato entrar nesta lista. Você pode usar {{nome}}, {{telefone}}, {{empresa}} para variáveis do contato."
                        rows={5}
                        className="auto-message-textarea"
                      />
                      <small className="field-hint">
                        Variáveis disponíveis: {'{nome}'} {'{primeiro_nome}'} {'{telefone}'} {'{empresa}'}
                      </small>
                    </div>
                  )}

                  {/* Preview da Mensagem */}
                  {groupNotificationMessage && selectedGroup && (
                    <div className="message-preview">
                      <label>Preview:</label>
                      <div className="preview-box">
                        {groupNotificationMessage
                          .replace(/\{\{nome\}\}/gi, 'João Silva')
                          .replace(/\{\{primeiro_nome\}\}/gi, 'João')
                          .replace(/\{\{telefone\}\}/gi, '11999999999')
                          .replace(/\{\{empresa\}\}/gi, 'Sua Empresa')}
                      </div>
                    </div>
                  )}

                  {/* Botão Salvar */}
                  {selectedGroup && (
                    <button
                      className="btn-save-auto-message"
                      onClick={handleSaveGroupNotification}
                      disabled={savingGroupNotification || !groupNotificationMessage.trim()}
                    >
                      <AppIcon name="save" />
                      {savingGroupNotification ? 'Salvando...' : 'Salvar Configuração'}
                    </button>
                  )}
                </div>
              )}

              <small className="section-description">
                Configure uma notificação que será enviada automaticamente para um grupo WhatsApp quando um contato entrar nesta lista. A mensagem incluirá os dados do contato.
              </small>
            </div>

            {/* Ferramenta de Remarketing */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="campaign" />
                  <h4>Salvar Lista para Remarketing</h4>
                </div>
              </div>

              <div className="toggle-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={remarketingEnabled}
                    onChange={(e) => setRemarketingEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {remarketingEnabled ? '✅ Remarketing ativo' : '❌ Remarketing inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {remarketingEnabled
                    ? 'Os contatos desta lista serão salvos para campanhas de remarketing'
                    : 'Nenhum contato será salvo para remarketing'}
                </small>
              </div>

              {remarketingEnabled && (
                <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                  {/* Nome da lista */}
                  <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.25rem', display: 'block' }}>
                      Nome da lista de remarketing
                    </label>
                    <input
                      type="text"
                      value={remarketingListName}
                      onChange={(e) => setRemarketingListName(e.target.value)}
                      placeholder="Ex: Remarketing - Novas Listas"
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.05)',
                        color: '#e2e8f0',
                        fontSize: '0.9rem',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Limpar lista após salvar */}
                  <div className="toggle-section" style={{ marginBottom: '1rem' }}>
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={remarketingClearAfterSave}
                        onChange={(e) => setRemarketingClearAfterSave(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        Limpar lista após salvar
                      </span>
                    </label>
                    <small className="toggle-description">
                      {remarketingClearAfterSave
                        ? '✅ Os cards serão removidos desta lista após serem salvos'
                        : '⚠️ Os cards permanecerão nesta lista'}
                    </small>
                  </div>

                  {/* Salvar Automaticamente */}
                  <div className="toggle-section" style={{ marginBottom: '1rem' }}>
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={remarketingAutoEnabled}
                        onChange={(e) => setRemarketingAutoEnabled(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        Salvar automaticamente
                      </span>
                    </label>
                    <small className="toggle-description">
                      {remarketingAutoEnabled
                        ? '⏱️ A lista será salva automaticamente a cada período definido'
                        : '📌 Salvar apenas manualmente'}
                    </small>
                  </div>

                  {/* Timer de Execução Automática */}
                  {remarketingAutoEnabled && (
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.25rem', display: 'block' }}>
                        Executar a cada quantas horas?
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="number"
                          min="1"
                          max="720"
                          value={remarketingTimerHours}
                          onChange={(e) => setRemarketingTimerHours(Math.max(1, parseInt(e.target.value) || 24))}
                          style={{
                            flex: 1,
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            background: 'rgba(255,255,255,0.05)',
                            color: '#e2e8f0',
                            fontSize: '0.9rem',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                        <span style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 500 }}>horas</span>
                      </div>
                      <small className="toggle-description" style={{ marginTop: '0.25rem' }}>
                        ⏰ A cada {remarketingTimerHours}h a lista será salva automaticamente
                      </small>
                    </div>
                  )}

                  {/* Botão de salvar */}
                  <button
                    type="button"
                    onClick={handleSaveRemarketingList}
                    disabled={savingRemarketing || !remarketingListName.trim()}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: savingRemarketing ? 'rgba(16, 185, 129, 0.3)' : 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: savingRemarketing ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease',
                      opacity: savingRemarketing ? 0.6 : 1
                    }}
                  >
                    {savingRemarketing ? '⏳ Salvando...' : '💾 Salvar Lista Agora'}
                  </button>

                  <small className="section-description" style={{ marginTop: '0.75rem', display: 'block' }}>
                    📊 Todos os contatos desta lista serão coletados com seus dados de formulário e informações de conexão para uso em campanhas de remarketing.
                  </small>
                </div>
              )}

              <small className="section-description">
                Salve os contatos desta lista para usar em campanhas de remarketing e automações futuras.
              </small>
            </div>
          </div>

          {/* Sidebar */}
          <div className="crm-modal-sidebar">
            <h4>Informações</h4>
            
            {/* Informações da conexão */}
            {connection && (
              <div className="info-box-sidebar">
                <div className="info-item">
                  <AppIcon name="phone" />
                  <div>
                    <strong>Conexão</strong>
                    <p>{connection.name}</p>
                    <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{connection.number}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="info-box-sidebar">
              <div className="info-item">
                <AppIcon name="credit_card" />
                <div>
                  <strong>Cards</strong>
                  <p>{list?.cards?.length || 0} itens</p>
                </div>
              </div>
              
              <div className="info-item">
                <AppIcon name="schedule" />
                <div>
                  <strong>Criada em</strong>
                  <p>{list?.created_at ? new Date(list.created_at).toLocaleDateString('pt-BR') : 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Dica sobre a lista */}
            <div className="info-box-sidebar tip-box">
              <div className="info-item">
                <AppIcon name="lightbulb" />
                <div>
                  <strong>Dica</strong>
                  <p>Use as listas de Follow-up para criar uma sequência de mensagens automáticas para seus leads.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="crm-modal-footer">
          <button className="btn-cancel-crm" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save-crm" 
            onClick={handleSave}
            disabled={loading}
          >
            <AppIcon name="check" />
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
